/**
 * Gaps — the questions the client assistant could not answer, clustered
 * nightly by cs-analysis (docs/design-knowledge-miner.md §6.11). Pure helpers
 * behind the Knowledge page's Gaps tab; exercised by knowledge_gaps.test.js
 * without a backend.
 *
 * Why the tab exists — Nikhil 2026-09-14: "Once these are done, you should
 * review questions where assistant could not find any answers.. I feel some
 * of them should be known by the knowledge engine". Until now an abstention
 * left no trace a rep could act on: the question was not close to any
 * proposal, so it produced no demand count and vanished the moment the
 * assistant said it could not help. The api now records each one, groups
 * them by meaning and ranks the groups breadth first — distinct orgs, then
 * how often, then recency — because a question three orgs ask is a hole in
 * the product's shared knowledge, while seven askings inside one org may be
 * that org's own workflow. The Gaps tab is where a rep turns such a question
 * into knowledge.
 *
 * Contract:
 *   GET /api/v1/knowledge/unanswered
 *     → { windowDays, similarity,
 *         counts: { questions, clustered, awaitingClustering, clusters, shown },
 *         clusters: [{ clusterKey, question, asked, orgs, orgNames, firstAskedAt, lastAskedAt,
 *                      portals, modules, emptyRetrievals, otherPhrasings, codeSuggestion }] }
 *     The order is the server's ranking and is kept. `codeSuggestion` is what the
 *     nightly gaps step made of the cluster from the product code — see the
 *     section below; null when it has not looked yet. `orgNames` is a sample
 *     (the api sends at most 5), `orgs` the exact count. `awaitingClustering`
 *     is the rows written since the last nightly run — counted, not listed,
 *     because they have no cluster yet. `emptyRetrievals` counts the askings
 *     where retrieval found nothing at all (vs. units that did not answer) —
 *     the strongest form of "we have nothing about this".
 *   POST /api/v1/knowledge/units — the same save the Chat thread's 💡 flow
 *     uses (CreateUnitBody); see gap_unit_body for what a gap can fill.
 */

export const UNANSWERED_PATH = '/api/v1/knowledge/unanswered';
export const UNITS_PATH = '/api/v1/knowledge/units';
/** Org names spelled out on a row before "+n" — enough to see who is asking without the row wrapping. */
export const ORGS_SHOWN = 3;
/** KNOWLEDGE_TRIGGERS_MAX in cs-analysis shared/knowledge.ts — the ArrayMaxSize on
 *  CreateUnitBody.triggers; a longer list is a 400, so the form sends at most this many. */
export const TRIGGERS_MAX = 12;
export const DEFAULT_MODULE = 'OTHER';

const str_or_null = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const int_or_zero = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0; };
const num_or_null = (v) => { if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const clean_list = (v) => (Array.isArray(v) ? v : []).map(str_or_null).filter(Boolean);
const ts_of = (d) => { const t = d instanceof Date ? d.getTime() : Date.parse(d); return Number.isFinite(t) ? t : null; };
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Comma / semicolon / newline separated trigger phrases → trimmed list.
 *  Shared with the Proposals edit form (index.jsx re-exports it). */
export const parse_triggers = (s) => String(s || '').split(/[,;\n]+/).map((t) => t.trim()).filter(Boolean);

// ---------------------------------------------------------------- the code's suggestion (§6.13)

/** `clusters[].codeSuggestion` — what the nightly gaps step made of the cluster
 *  from the product code (docs/design-knowledge-miner.md §6.13). Additive since
 *  2026-09-16, null when no finished attempt covers the cluster:
 *    { attemptId, derivedAt, outcome, failedGate, note,
 *      unit: { id, module, feature, title, body, corroboration, audience, status } | null,
 *      answeredBy: [{ id, title }], files: [{ repo, path, sha }], unreleased: { files, byRepo } | null }
 *  outcome: unverified (a draft that failed a gate — failedGate safety / judge /
 *  replay / flagged / demoted, the gate's reason in note) · would_promote (every
 *  gate passed, shadow mode) · code_silent (the code does not say; no draft) ·
 *  unreleased (the answer is on the code's default branch and not in production
 *  yet — `unreleased` counts the hit files per repository; no draft, no files) ·
 *  answered_now (the assistant already answers it from the units in answeredBy;
 *  no draft) · client_safe (promoted, serving) · withdrawn (promoted, then
 *  demoted / retired / stale). Shown to staff only — the CSP is. */
export const CODE_OUTCOMES = ['unverified', 'would_promote', 'code_silent', 'unreleased', 'answered_now', 'client_safe', 'withdrawn'];
export const CODE_GATES = ['safety', 'judge', 'replay', 'flagged', 'demoted'];
/** The gate in words after "failed" — the api's names are the step's internals
 *  (flagged = a rep flagged the promoted unit, demoted = clients demoted it recently). */
export const GATE_WORDS = { safety: 'the safety screen', judge: 'the code judge', replay: 'the replay', flagged: 'the rep-flag check', demoted: 'the demotion check' };
/** A draft or unit title quoted inside a status line is cut here. */
export const CODE_TITLE_CHARS = 100;
/** Characters of a draft body shown before "more" — about four lines of the card. */
export const CODE_BODY_PREVIEW = 360;

const cut = (s, n) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? t.slice(0, n - 1) + '…' : t; };

/** The suggestion as the card trusts it: a known outcome, else null; the gate
 *  only when it is one the step has; the draft only when it has a title or a
 *  body (newlines kept — it is shown pre-wrap); files only with a path;
 *  unreleased counts as non-negative integers, repos without hits dropped. */
export const normalize_code_suggestion = (v) => {
    if (!v || typeof v !== 'object' || CODE_OUTCOMES.indexOf(v.outcome) < 0) return null;
    const u = v.unit && typeof v.unit === 'object' ? v.unit : null;
    const title = u ? str_or_null(u.title) || '' : '';
    const body = u ? String(u.body || '').trim() : '';
    const derived = ts_of(v.derivedAt);
    const files = (Array.isArray(v.files) ? v.files : [])
        .map((f) => (f && typeof f === 'object' ? { repo: str_or_null(f.repo) || '', path: str_or_null(f.path) || '', sha: str_or_null(f.sha) || '' } : null))
        .filter((f) => f && f.path);
    const answered_by = (Array.isArray(v.answeredBy) ? v.answeredBy : [])
        .map((a) => (a && typeof a === 'object' ? { id: str_or_null(a.id) || '', title: str_or_null(a.title) || '' } : null))
        .filter((a) => a && (a.id || a.title));
    const ur = v.unreleased && typeof v.unreleased === 'object' ? v.unreleased : null;
    const by_repo = {};
    if (ur && ur.byRepo && typeof ur.byRepo === 'object') {
        Object.keys(ur.byRepo).forEach((k) => { const n = int_or_zero(ur.byRepo[k]); if (k.trim() && n > 0) by_repo[k.trim()] = n; });
    }
    return {
        attemptId: str_or_null(v.attemptId),
        derivedAt: derived === null ? null : new Date(derived).toISOString(),
        outcome: v.outcome,
        failedGate: CODE_GATES.indexOf(v.failedGate) >= 0 ? v.failedGate : null,
        note: str_or_null(v.note) || '',
        unit: u && (title || body) ? {
            id: str_or_null(u.id), module: str_or_null(u.module), feature: str_or_null(u.feature) || '', title, body,
            corroboration: str_or_null(u.corroboration), audience: str_or_null(u.audience), status: str_or_null(u.status),
        } : null,
        answeredBy: answered_by,
        files,
        unreleased: ur ? { files: int_or_zero(ur.files), byRepo: by_repo } : null,
    };
};

/** "3 files: infurnia/frontend 2, infurnia-main 1" — the hit files ahead of
 *  production, per repository (most first); "2 files in infurnia/frontend" for
 *  one repo; the total alone when the split is missing; '' when nothing is. */
export const unreleased_counts = (ur) => {
    if (!ur || typeof ur !== 'object') return '';
    const by_repo = ur.byRepo && typeof ur.byRepo === 'object' ? ur.byRepo : {};
    const repos = Object.keys(by_repo).map((k) => [k, int_or_zero(by_repo[k])]).filter(([k, n]) => k.trim() && n > 0).sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : 0));
    const total = int_or_zero(ur.files) || repos.reduce((s, [, n]) => s + n, 0);
    if (total === 0) return '';
    if (repos.length === 1) return `${plural(total, 'file')} in ${repos[0][0]}`;
    return `${plural(total, 'file')}${repos.length ? `: ${repos.map(([k, n]) => `${k} ${n}`).join(', ')}` : ''}`;
};

/** The one-line status a rep reads first, per outcome:
 *    unverified    "The code suggests “<draft title>” (unverified — failed <gate>)"
 *    would_promote "Would promote (shadow)"
 *    code_silent   "The code does not say"
 *    answered_now  "The assistant now answers this — from “<unit>”, “<unit>”"
 *    client_safe   "Client-safe"
 *    withdrawn     "Withdrawn"
 *    unreleased    "Exists in the code's main branch, not in production yet — <per-repo counts>"
 *  A draft without a title reads "an answer"; an unverified without a known
 *  gate has no "failed" part; unknown outcome or nothing → ''. */
export const code_suggestion_label = (cs) => {
    if (!cs || typeof cs !== 'object') return '';
    switch (cs.outcome) {
        case 'unverified': {
            const title = cs.unit ? cut(cs.unit.title, CODE_TITLE_CHARS) : '';
            const gate = GATE_WORDS[cs.failedGate];
            return `The code suggests ${title ? `“${title}”` : 'an answer'} (unverified${gate ? ` — failed ${gate}` : ''})`;
        }
        case 'would_promote': return 'Would promote (shadow)';
        case 'code_silent': return 'The code does not say';
        case 'answered_now': {
            const titles = (Array.isArray(cs.answeredBy) ? cs.answeredBy : []).map((a) => a && str_or_null(a.title)).filter(Boolean).map((t) => `“${cut(t, CODE_TITLE_CHARS)}”`);
            return `The assistant now answers this${titles.length ? ` — from ${titles.join(', ')}` : ''}`;
        }
        case 'client_safe': return 'Client-safe';
        case 'withdrawn': return 'Withdrawn';
        case 'unreleased': {
            const counts = unreleased_counts(cs.unreleased);
            return `Exists in the code's main branch, not in production yet${counts ? ` — ${counts}` : ''}`;
        }
        default: return '';
    }
};

/** How the status line is styled: warn for a draft that failed a gate, ok for
 *  one that passed (or would), info for "the code has it but production / the
 *  assistant already covers it", muted for silence and a withdrawn unit. */
export const code_suggestion_tone = (cs) => {
    switch (cs && cs.outcome) {
        case 'unverified': return 'warn';
        case 'would_promote': case 'client_safe': return 'ok';
        case 'unreleased': case 'answered_now': return 'info';
        case 'code_silent': case 'withdrawn': return 'muted';
        default: return '';
    }
};

/** The gate's recorded reason: "Failed the safety screen: <note>" for an
 *  unverified draft (the note alone when the gate is unknown, the gate alone
 *  when there is no note), the note as it is for every other outcome, '' when none. */
export const code_suggestion_note = (cs) => {
    if (!cs || typeof cs !== 'object') return '';
    const note = str_or_null(cs.note) || '';
    const gate = cs.outcome === 'unverified' ? GATE_WORDS[cs.failedGate] : null;
    if (!gate) return note;
    const failed = `Failed ${gate}`;
    return note ? `${failed}: ${note}` : failed;
};

/** The cited files as "repo/path" lines, de-duplicated, order kept; [] when none. */
export const code_suggestion_paths = (cs) => {
    const files = cs && Array.isArray(cs.files) ? cs.files : [];
    const out = [];
    files.forEach((f) => {
        const path = f ? str_or_null(f.path) : null;
        if (!path) return;
        const repo = str_or_null(f.repo);
        const line = repo ? `${repo}/${path}` : path;
        if (out.indexOf(line) < 0) out.push(line);
    });
    return out;
};

/** The draft body for the card: whole when it fits CODE_BODY_PREVIEW or the
 *  reader opened it, else cut with an ellipsis; `collapsible` says whether a
 *  more / less toggle is needed at all. Newlines are kept. */
export const code_suggestion_body = (cs, open = false) => {
    const body = cs && cs.unit ? String(cs.unit.body || '').trim() : '';
    const collapsible = body.length > CODE_BODY_PREVIEW;
    return { text: collapsible && !open ? body.slice(0, CODE_BODY_PREVIEW - 1).trimEnd() + '…' : body, collapsible };
};

/** One cluster as the tab trusts it: key + question as strings, counts as
 *  non-negative integers, lists cleaned, dates kept as ISO strings or null.
 *  Null when the row has no key or no question — nothing to show or act on. */
export const normalize_gap = (c) => {
    if (!c || typeof c !== 'object') return null;
    const cluster_key = c.clusterKey === null || c.clusterKey === undefined || c.clusterKey === '' ? null : String(c.clusterKey);
    const question = str_or_null(c.question);
    if (!cluster_key || !question) return null;
    const first = ts_of(c.firstAskedAt);
    const last = ts_of(c.lastAskedAt);
    return {
        clusterKey: cluster_key,
        question,
        asked: int_or_zero(c.asked),
        orgs: int_or_zero(c.orgs),
        orgNames: clean_list(c.orgNames),
        firstAskedAt: first === null ? null : new Date(first).toISOString(),
        lastAskedAt: last === null ? null : new Date(last).toISOString(),
        portals: clean_list(c.portals),
        modules: clean_list(c.modules),
        emptyRetrievals: int_or_zero(c.emptyRetrievals),
        otherPhrasings: clean_list(c.otherPhrasings).filter((p) => p.toLowerCase() !== question.toLowerCase()),
        codeSuggestion: normalize_code_suggestion(c.codeSuggestion),
    };
};

/** GET /knowledge/unanswered → {windowDays, similarity, counts, clusters}.
 *  Rows without a key or question are dropped; a count the server omits
 *  falls back to what is in hand (`clusters` / `shown` to the list length,
 *  the rest to 0). Junk → the empty response. Server order kept. */
export const normalize_unanswered = (resp) => {
    const r = resp && typeof resp === 'object' ? resp : {};
    const clusters = (Array.isArray(r.clusters) ? r.clusters : []).map(normalize_gap).filter(Boolean);
    const c = r.counts && typeof r.counts === 'object' ? r.counts : {};
    const has = (k) => num_or_null(c[k]) !== null;
    return {
        windowDays: num_or_null(r.windowDays),
        similarity: num_or_null(r.similarity),
        counts: {
            questions: int_or_zero(c.questions),
            clustered: int_or_zero(c.clustered),
            awaitingClustering: int_or_zero(c.awaitingClustering),
            clusters: has('clusters') ? int_or_zero(c.clusters) : clusters.length,
            shown: has('shown') ? int_or_zero(c.shown) : clusters.length,
        },
        clusters,
    };
};

/** The tab's number: clusters in the window (the exact count, not the page); null while loading. */
export const gaps_total = (d) => (d ? d.counts.clusters : null);

/** "42 questions in the last 30 days · 9 clusters · 3 awaiting tonight's
 *  clustering · showing the top 50". The awaiting part only when there is
 *  something waiting, the "top N" part only when the page is shorter than the
 *  window's cluster count; no window → no "in the last … days". */
export const gap_summary_line = (counts, window_days) => {
    const c = counts || {};
    const days = num_or_null(window_days);
    const parts = [`${plural(int_or_zero(c.questions), 'question')}${days && days > 0 ? ` in the last ${plural(days, 'day')}` : ''}`, plural(int_or_zero(c.clusters), 'cluster')];
    const awaiting = int_or_zero(c.awaitingClustering);
    if (awaiting > 0) parts.push(`${awaiting} awaiting tonight's clustering`);
    const shown = int_or_zero(c.shown);
    if (shown > 0 && shown < int_or_zero(c.clusters)) parts.push(`showing the top ${shown}`);
    return parts.join(' · ');
};

/** What an empty list means: either nothing has been clustered yet (the
 *  nightly run has not seen tonight's questions) or the assistant really
 *  answered everything in the window. */
export const empty_gaps_text = (counts, window_days) => {
    const awaiting = int_or_zero(counts && counts.awaitingClustering);
    if (awaiting > 0) return `Nothing clustered yet — ${plural(awaiting, 'question')} ${awaiting === 1 ? 'is' : 'are'} waiting for tonight's clustering.`;
    const days = num_or_null(window_days);
    return `The assistant answered everything it was asked${days && days > 0 ? ` in the last ${plural(days, 'day')}` : ' in this window'}.`;
};

/** "Acme, Beta, Gamma +2" — the first `max` names, then how many more.
 *  `total` is the exact org count when known (the names are only a sample);
 *  without it the remainder is counted off the names in hand. */
export const orgs_label = (org_names, max = ORGS_SHOWN, total = null) => {
    const names = clean_list(org_names);
    const shown = names.slice(0, Math.max(0, max));
    const exact = num_or_null(total);
    const rest = Math.max(0, (exact !== null && exact >= shown.length ? Math.round(exact) : names.length) - shown.length);
    return shown.join(', ') + (rest > 0 && shown.length ? ` +${rest}` : '');
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "02 Sep", or "28 Dec 2025" when the year is not the current one (the
 *  window is 30 days, so that only shows around New Year). Hand-rolled: the
 *  runtime's short month for en-IN is "Sept", and it varies by ICU build. */
export const fmt_day = (d, now = new Date()) => {
    const t = ts_of(d);
    if (t === null) return '';
    const date = new Date(t);
    const ref = now instanceof Date ? now : new Date(now);
    const year = date.getFullYear() === ref.getFullYear() ? '' : ` ${date.getFullYear()}`;
    return `${String(date.getDate()).padStart(2, '0')} ${MONTHS[date.getMonth()]}${year}`;
};

/** "asked 4× · 3 orgs: Acme, Beta, Gamma · first 02 Sep · last 14 Sep" — one
 *  asking on one day collapses to "on 14 Sep". Parts without data are left out. */
export const gap_meta_line = (c, now = new Date()) => {
    if (!c) return '';
    const parts = [`asked ${int_or_zero(c.asked)}×`];
    const orgs = int_or_zero(c.orgs);
    if (orgs > 0) {
        const names = orgs_label(c.orgNames, ORGS_SHOWN, orgs);
        parts.push(`${plural(orgs, 'org')}${names ? `: ${names}` : ''}`);
    }
    const first = fmt_day(c.firstAskedAt, now);
    const last = fmt_day(c.lastAskedAt, now);
    if (first && last && first !== last) parts.push(`first ${first}`, `last ${last}`);
    else if (first || last) parts.push(`on ${first || last}`);
    return parts.join(' · ');
};

/** "retrieved nothing 3×" — the strongest gap signal (retrieval itself came
 *  back empty, not just units that failed to answer); '' when it never did. */
export const empty_retrievals_label = (n) => { const k = int_or_zero(n); return k > 0 ? `retrieved nothing ${k}×` : ''; };
export const EMPTY_RETRIEVALS_HINT = 'How many of these askings found no knowledge unit at all — not even one that failed to answer';

/** "3 other phrasings ▸" / "… ▾"; '' when there are none to show. */
export const phrasings_label = (n, open) => { const k = int_or_zero(n); return k > 0 ? `${plural(k, 'other phrasing')} ${open ? '▾' : '▸'}` : ''; };

/** The filter box: a case-insensitive substring over the question, the other
 *  phrasings and the org names. Blank → everything, in the server's order. */
export const filter_gaps = (clusters, q) => {
    const list = Array.isArray(clusters) ? clusters.filter(Boolean) : [];
    const needle = String(q || '').trim().toLowerCase();
    if (!needle) return list;
    const hit = (s) => typeof s === 'string' && s.toLowerCase().includes(needle);
    return list.filter((c) => hit(c.question) || (Array.isArray(c.otherPhrasings) && c.otherPhrasings.some(hit)) || (Array.isArray(c.orgNames) && c.orgNames.some(hit)));
};

/** The "Write this answer" form, seeded from the cluster (PromoteKnowledgeModal's
 *  form shape): the question is the title and the first trigger, the other
 *  phrasings are the rest of the triggers (they are literally how clients
 *  phrase it — capped at TRIGGERS_MAX so what the rep sees is what is sent),
 *  the module is the cluster's when it has exactly one that the taxonomy
 *  knows (the portal records it free-form, so case is folded), else OTHER.
 *  Client-safe by default — the point of answering a gap is that the client
 *  assistant can answer it next time. The fact itself is the rep's. */
export const gap_form_seed = (c, modules = []) => {
    const question = c ? str_or_null(c.question) || '' : '';
    const known = Array.isArray(modules) ? modules : [];
    const own = c && Array.isArray(c.modules) ? clean_list(c.modules) : [];
    const module = own.length === 1 && known.indexOf(own[0].toUpperCase()) >= 0 ? own[0].toUpperCase() : DEFAULT_MODULE;
    const triggers = [question, ...(c && Array.isArray(c.otherPhrasings) ? clean_list(c.otherPhrasings) : [])].filter(Boolean).slice(0, TRIGGERS_MAX);
    return { title: question.slice(0, 200), module, feature: '', body: '', triggers: triggers.join(', '), clientSafe: true };
};

/** "Write this answer" when the code drafted one: gap_form_seed, then the
 *  draft's title, body and feature over it and its module when the taxonomy
 *  knows it (else the cluster's). The triggers stay the clients' phrasings,
 *  client-safe stays on, and nothing here is provenance — gap_unit_body still
 *  sends gap.clusterKey, so the save closes the cluster exactly as a
 *  hand-written answer does. No draft (code_silent / unreleased / answered_now
 *  or no suggestion) → the plain seed. */
export const gap_form_seed_from_suggestion = (c, cs, modules = []) => {
    const seed = gap_form_seed(c, modules);
    const u = cs && cs.unit && typeof cs.unit === 'object' ? cs.unit : null;
    if (!u) return seed;
    const known = Array.isArray(modules) ? modules : [];
    const own = str_or_null(u.module);
    return {
        ...seed,
        title: (str_or_null(u.title) || seed.title).slice(0, 200),
        body: String(u.body || '').trim(),
        feature: str_or_null(u.feature) || seed.feature,
        module: own && known.indexOf(own.toUpperCase()) >= 0 ? own.toUpperCase() : seed.module,
    };
};

/** The form cannot be sent without a title and the fact itself (the api's own rule). */
export const gap_form_invalid = (form) => !form || !String(form.title || '').trim() || !String(form.body || '').trim();

/** The POST /knowledge/units body a gap can fill, in CreateUnitBody's
 *  vocabulary: module · feature · title · body · triggers (deduplicated,
 *  capped) · clientSafe. Strings trimmed.
 *
 *  Provenance caveat: CreateUnitBody also carries `source` — a conversation
 *  message {channelId, seq, fromSeq, toSeq} — and the api looks that message
 *  up before saving. A gap has no message (the question was asked of the
 *  portal assistant), so nothing is sent for it here; until the api accepts a
 *  gap as provenance the save is refused and the row shows the api's answer
 *  with the rep's text intact. */
export const gap_unit_body = (form, cluster_key) => {
    const f = form || {};
    const seen = new Set();
    const triggers = parse_triggers(f.triggers).filter((t) => { const k = t.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; }).slice(0, TRIGGERS_MAX);
    const key = cluster_key === null || cluster_key === undefined || cluster_key === '' ? null : String(cluster_key);
    return {
        module: str_or_null(f.module) || DEFAULT_MODULE,
        feature: String(f.feature || '').trim(),
        title: String(f.title || '').trim(),
        body: String(f.body || '').trim(),
        triggers,
        clientSafe: !!f.clientSafe,
        // Provenance: the api's CreateUnitBody takes `source` (a conversation
        // message) OR `gap` (an unanswered-question cluster); a gap has no
        // message, and the cluster key lets the server mark it answered for
        // everyone, not just this session.
        ...(key ? { gap: { clusterKey: key } } : {}),
    };
};

/** One-line receipt for an answered row. `local` is this session's save for
 *  the cluster: {pending} while the POST is in flight, then {existing, unit};
 *  {error} is a refused save — no receipt, the row shows the form again. */
export const gap_receipt_text = (local) => {
    if (!local || local.error) return '';
    if (local.pending) return '✓ saving…';
    return local.existing ? '✓ answered — already known, the existing fact was confirmed instead' : '✓ answered — saved as a rep-attested fact';
};

/** True once this session saved the cluster's answer (pending counts — the
 *  row already reads as a receipt); a refused save does not. */
export const gap_answered = (local) => !!local && !local.error;

export const gap_dom_id = (cluster_key) => `knowledge-gap-${cluster_key}`;
